import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Search, Filter } from 'lucide-react';
import { getCalls } from '../services/api';
import { timeAgo, formatDuration, callTypeBadge, urgencyBadge, sentimentEmoji } from '../utils';

const FILTERS = [
  { key: 'all', label: 'All Calls' },
  { key: 'sales_lead', label: 'Sales Leads' },
  { key: 'service_request', label: 'Service' },
  { key: 'enquiry', label: 'Enquiry' },
  { key: 'complaint', label: 'Complaints' },
];

export default function CallsList() {
  const [calls, setCalls] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const navigate = useNavigate();

  const fetchCalls = () => {
    getCalls().then(res => {
      setCalls(res.data.calls || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
    setLastRefresh(Date.now());
  };

  useEffect(() => {
    fetchCalls();
    // Auto-refresh every 30 seconds to show new calls and completed pipelines
    const interval = setInterval(fetchCalls, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let result = calls;
    if (activeFilter !== 'all') {
      result = result.filter(c => c.call_type === activeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        (c.caller_name || '').toLowerCase().includes(q) ||
        (c.caller_company || '').toLowerCase().includes(q) ||
        (c.caller_number || '').includes(q) ||
        (c.summary || '').toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [calls, activeFilter, search]);

  return (
    <div className="page-wrap">
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Calls</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
            {calls.length} total calls captured
          </p>
        </div>
        <button
          onClick={fetchCalls}
          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 12px', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Search + Filters */}
      <div style={{ marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div className="search-bar-wrap">
          <Search size={14} color="var(--text-muted)" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search calls..."
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: 13,
              flex: 1,
              minWidth: 0,
            }}
          />
        </div>

        <div className="filter-bar">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={activeFilter === f.key ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ fontSize: 12, padding: '6px 12px' }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Phone size={32} color="var(--text-muted)" style={{ marginBottom: 12 }} />
            {search || activeFilter !== 'all' ? (
              <>
                <p style={{ fontWeight: 600, marginBottom: 6 }}>No calls match your search</p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Try adjusting your filters or search term</p>
              </>
            ) : (
              <>
                <p style={{ fontWeight: 600, marginBottom: 6 }}>No calls captured yet</p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                  Once you set up call forwarding, every call will appear here automatically
                </p>
                <a href="/team" style={{
                  display: 'inline-block', padding: '8px 20px', borderRadius: 6,
                  background: 'var(--blue)', color: '#fff', fontSize: 13,
                  fontWeight: 600, textDecoration: 'none',
                }}>
                  Set up your team →
                </a>
              </>
            )}
          </div>
        ) : (
          <table className="resp-table">
            <thead>
              <tr>
                <th>Caller</th>
                <th>Type</th>
                <th className="desktop-only">Summary</th>
                <th className="desktop-only">Duration</th>
                <th>Urgency</th>
                <th className="desktop-only">Mood</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(call => {
                const typeBadge = callTypeBadge(call.call_type);
                const urgBadge = urgencyBadge(call.urgency);
                return (
                  <tr key={call.id} onClick={() => navigate(`/calls/${call.id}`)}>
                    <td>
                      <div style={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 220,
                      }}>
                        {call.caller_name || call.caller_number || '—'}
                      </div>
                      {call.caller_company && (
                        <div style={{
                          fontSize: 11,
                          color: 'var(--text-muted)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 220,
                        }}>
                          {call.caller_company}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${typeBadge.class}`}>{typeBadge.label}</span>
                    </td>
                    <td className="desktop-only" style={{ maxWidth: 280 }}>
                      <div style={{
                        color: 'var(--text-secondary)',
                        fontSize: 12,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 260,
                      }}>
                        {call.summary ? call.summary.split('\n')[0].substring(0, 100) : '—'}
                      </div>
                    </td>
                    <td className="desktop-only" style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-secondary)' }}>
                      {formatDuration(call.duration)}
                    </td>
                    <td>
                      <span className={`badge ${urgBadge.class}`}>{urgBadge.label}</span>
                    </td>
                    <td className="desktop-only" style={{ fontSize: 16 }}>
                      {sentimentEmoji(call.sentiment)}
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {!call.transcript_english && !call.summary && call.recording_url ? (
                        <span style={{ color: 'var(--orange)', fontSize: 11 }}>Processing...</span>
                      ) : timeAgo(call.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
